import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { Alert, Box, Grid, Paper, Stack, Typography } from '@mui/material'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import RequestQuoteIcon from '@mui/icons-material/RequestQuote'
import Inventory2Icon from '@mui/icons-material/Inventory2'
import PaidIcon from '@mui/icons-material/Paid'
import API from '../../../services/API'
import { TenantSettingsContext } from '../../../App'
import StatCard from '../../../components/Cards/StatCard'
import { vatMultiplierForItem } from '../../../helpers/region'

const lineItemsTotal = (items, region) =>
  (items || []).reduce((total, item) => {
    const vatMultiplier = vatMultiplierForItem(item.vat, region)
    return (
      total +
      (Number(item.quantity) || 0) *
        (Number(item.unitPrice) || 0) *
        vatMultiplier
    )
  }, 0)

// VAT-inclusive gross, net of credit notes and the flat discount - matches
// the server's netAmount maths
const getInvoiceTotal = (invoice, region) => {
  const credited = (invoice?.creditNotes || []).reduce(
    (total, note) => total + lineItemsTotal(note.items, region),
    0,
  )
  return (
    lineItemsTotal(invoice?.items, region) -
    credited -
    Number(invoice?.discount || 0)
  )
}

// Stored status is authoritative (a scheduler flips invoices to Overdue);
// the Sent/Partially Paid past-due check covers invoices from before
// statuses were stored reliably
const isOverdue = (invoice) =>
  invoice.status === 'Overdue' ||
  (['Sent', 'Partially Paid'].includes(invoice.status) &&
    moment(invoice.dueDate).isBefore(moment(), 'day'))

const QuickLink = ({ icon: Icon, title, caption, onClick }) => (
  <Paper
    variant="outlined"
    onClick={onClick}
    sx={{
      p: 2,
      borderRadius: 3,
      cursor: 'pointer',
      transition: 'transform 160ms ease, box-shadow 160ms ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 12px 28px rgba(15,23,42,0.08)',
      },
    }}
  >
    <Stack direction="row" gap={1.5} alignItems="center">
      <Icon color="anBlue" />
      <Stack spacing={0}>
        <Typography variant="body1" fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      </Stack>
    </Stack>
  </Paper>
)

const FinanceCentre = () => {
  const navigate = useNavigate()
  const { orgConfig, tenantInfo } = useContext(TenantSettingsContext)
  const currency = orgConfig?.orgSettings?.currency ?? '£'
  const region = tenantInfo?.region

  const [invoices, setInvoices] = useState([])
  const [quotes, setQuotes] = useState([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)

  useEffect(() => {
    setLoading(true)
    setFetchError(false)
    Promise.all([API.get('/finance/invoices'), API.get('/finance/quotes')])
      .then(([invoicesRes, quotesRes]) => {
        if (invoicesRes.status === 200 && Array.isArray(invoicesRes.data)) {
          setInvoices(invoicesRes.data)
        } else {
          setFetchError(invoicesRes?.data?.error || true)
        }
        if (quotesRes.status === 200 && Array.isArray(quotesRes.data)) {
          setQuotes(quotesRes.data)
        } else {
          setFetchError((prev) => prev || quotesRes?.data?.error || true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [])

  const summary = useMemo(() => {
    const invoiceTotals = invoices.reduce(
      (acc, invoice) => {
        const total = getInvoiceTotal(invoice, region)
        const paid = Number(invoice.totalPaid || 0)
        const outstanding = Math.max(0, total - paid)
        acc.outstanding += outstanding
        if (isOverdue(invoice)) {
          acc.overdue += outstanding
        }
        return acc
      },
      { outstanding: 0, overdue: 0 },
    )

    const quotesAwaitingResponse = quotes.filter(
      (q) => q.status === 'Sent',
    ).length

    return { ...invoiceTotals, quotesAwaitingResponse }
  }, [invoices, quotes, region])

  return (
    <Stack spacing={3}>
      <Stack spacing={0.5}>
        <Typography variant="title2">Finance Centre</Typography>
        <Typography variant="body2" color="text.secondary">
          Everything money-related in one place — invoicing, quoting, and
          consumable costs.
        </Typography>
      </Stack>
      {fetchError && (
        <Alert severity="error">
          {typeof fetchError === 'string'
            ? fetchError
            : 'Unable to load the finance figures right now. Please try again later.'}
        </Alert>
      )}
      <Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={ReceiptLongIcon}
              label="Outstanding"
              value={
                loading ? '—' : `${currency}${summary.outstanding.toFixed(2)}`
              }
              color="anBlue"
              onClick={() => navigate('/invoices')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={WarningAmberIcon}
              label="Overdue"
              value={loading ? '—' : `${currency}${summary.overdue.toFixed(2)}`}
              color="anRed"
              onClick={() => navigate('/unpaid-invoices')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={RequestQuoteIcon}
              label="Quotes Awaiting Response"
              value={loading ? '—' : String(summary.quotesAwaitingResponse)}
              color="anPurple"
              onClick={() => navigate('/finance/quotes')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={PaidIcon}
              label="Total Quotes Tracked"
              value={loading ? '—' : String(quotes.length)}
              color="anGreen"
              onClick={() => navigate('/finance/quotes')}
            />
          </Grid>
        </Grid>
      </Box>

      <Stack spacing={1.5}>
        <Typography variant="title3">Go to</Typography>
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <QuickLink
                icon={ReceiptLongIcon}
                title="Invoices"
                caption="All event and PTS invoices"
                onClick={() => navigate('/invoices')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <QuickLink
                icon={RequestQuoteIcon}
                title="Quotes"
                caption="All event and PTS quotes"
                onClick={() => navigate('/finance/quotes')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <QuickLink
                icon={WarningAmberIcon}
                title="Unpaid Invoices"
                caption="Chase what's overdue"
                onClick={() => navigate('/unpaid-invoices')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <QuickLink
                icon={Inventory2Icon}
                title="Stock Catalog"
                caption="Consumable unit costs for make-ready"
                onClick={() => navigate('/site-configuration/stock-catalog')}
              />
            </Grid>
          </Grid>
        </Box>
      </Stack>
    </Stack>
  )
}

export default FinanceCentre
